import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import { useRules } from '../context/RuleContext';

const RULE_FIELDS = [
  ['carrier', 'Carrier'], ['origin', 'Origin'], ['destination', 'Destination'],
  ['feePercent', 'Fee %'], ['feeAmount', 'Fee Amount'], ['oncePerBookingFeeAmount', 'Once/Booking Fee'], ['suppress', 'Suppress'],
  ['tripType', 'Trip Type'], ['cabinClass', 'Cabin'], ['cabinClassMatchMode', 'Cabin Match'], ['passengerType', 'Passenger'],
  ['priceSource', 'Price Source'], ['minPrice', 'Min Price'], ['maxPrice', 'Max Price'],
  ['interline', 'Interline'], ['sliceInterline', 'Slice Interline'], ['minSegments', 'Min Segments'], ['minTotalSegments', 'Min Total Segments'],
  ['layoverMinutes', 'Layover Min'], ['maxHoursToDeparture', 'Max Hrs to Departure'],
  ['travelDateEffective', 'Travel From'], ['travelDateDiscontinue', 'Travel To'],
  ['ticketingDateEffective', 'Ticketing From'], ['ticketingDateDiscontinue', 'Ticketing To'],
];

function RuleDetails({ rule, viaGroup }) {
  return (
    <Box sx={{ p: 0.5, minWidth: 220 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Rule #{rule.sequenceNum}</Typography>
      {viaGroup && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#ffcc80' }}>
          Matched via carrier group {rule.carrier}
        </Typography>
      )}
      {RULE_FIELDS.filter(([k]) => rule[k] != null && rule[k] !== '').map(([k, label]) => (
        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12 }}>
          <span style={{ opacity: 0.75 }}>{label}</span>
          <span style={{ fontWeight: 600 }}>{String(rule[k])}{k === 'feePercent' ? '%' : ''}</span>
        </Box>
      ))}
    </Box>
  );
}

function GroupMembers({ title, members, highlight }) {
  return (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{title} — {members.length}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {members.map(m => (
          <Chip key={m} label={m} size="small"
            sx={{ height: 20, fontSize: 11, color: 'white', bgcolor: m === highlight ? 'warning.main' : 'rgba(255,255,255,0.15)', fontWeight: m === highlight ? 700 : 400 }} />
        ))}
      </Box>
    </Box>
  );
}

export default function BulkEdit() {
  const { feeRules, bulkExtendDates, carrierGroups, locationGroups, updateCarrierGroup } = useRules();
  const [hoveredSeq, setHoveredSeq] = useState(null);
  const [groupHover, setGroupHover] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // === Bulk Date Extension State ===
  const [selectedRules, setSelectedRules] = useState([]);
  const [newTravelDate, setNewTravelDate] = useState('');
  const [newTicketingDate, setNewTicketingDate] = useState('');
  const [filterCarrier, setFilterCarrier] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterCabin, setFilterCabin] = useState('');

  // === Carrier Group Edit State ===
  const [selectedGroup, setSelectedGroup] = useState('');
  const [carrierToAdd, setCarrierToAdd] = useState('');
  const [carrierToRemove, setCarrierToRemove] = useState('');

  // Filter rules that have date fields
  const datedRules = useMemo(() => {
    let rules = feeRules.filter(r =>
      r.travelDateDiscontinue || r.ticketingDateDiscontinue || r.travelDateEffective
    );
    if (filterCarrier) {
      // Also match rules whose carrier group (U:N) contains the airline, e.g. AC -> U:33
      const q = filterCarrier.trim().toUpperCase();
      rules = rules.filter(r => {
        if (r.carrier.toUpperCase().includes(q)) return true;
        const group = carrierGroups.find(g => `U:${g.tableNum}` === r.carrier);
        return !!group && group.carriers.some(c => c.toUpperCase() === q);
      });
    }
    if (filterOrigin) {
      rules = rules.filter(r => r.origin && r.origin.toLowerCase().includes(filterOrigin.toLowerCase()));
    }
    if (filterCabin) {
      rules = rules.filter(r => r.cabinClass === filterCabin);
    }
    return rules;
  }, [feeRules, carrierGroups, filterCarrier, filterOrigin, filterCabin]);

  const carrierGroupOf = (ref) => carrierGroups.find(g => `U:${g.tableNum}` === ref);
  const locationGroupOf = (ref) => locationGroups.find(g => `U:${g.tableNum}` === ref);
  const filterCode = filterCarrier.trim().toUpperCase();

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRules(datedRules.map(r => r.sequenceNum));
    } else {
      setSelectedRules([]);
    }
  };

  const handleSelectRule = (seqNum) => {
    setSelectedRules(prev =>
      prev.includes(seqNum) ? prev.filter(s => s !== seqNum) : [...prev, seqNum]
    );
  };

  const handleBulkExtend = () => {
    if (selectedRules.length === 0) return;
    if (!newTravelDate && !newTicketingDate) return;
    bulkExtendDates(selectedRules, newTravelDate || null, newTicketingDate || null);
    setSelectedRules([]);
  };

  // === Carrier Group Management ===
  const currentGroup = carrierGroups.find(g => g.tableNum === selectedGroup);

  const handleAddCarrier = () => {
    if (!currentGroup || !carrierToAdd.trim()) return;
    const code = carrierToAdd.trim().toUpperCase();
    if (!/^[A-Z0-9]{2}$/.test(code)) {
      return;
    }
    if (currentGroup.carriers.includes(code)) return;
    updateCarrierGroup(selectedGroup, [...currentGroup.carriers, code]);
    setCarrierToAdd('');
  };

  const handleRemoveCarrier = () => {
    if (!currentGroup || !carrierToRemove) return;
    updateCarrierGroup(selectedGroup, currentGroup.carriers.filter(c => c !== carrierToRemove));
    setCarrierToRemove('');
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Bulk Operations</Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Extend Rule Dates" />
        <Tab label="Manage Carrier Groups" />
      </Tabs>

      {/* Tab 0: Bulk Date Extension */}
      {tabValue === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Select rules below and set new expiration dates. This is commonly used to extend rule validity periods.
            Only rules with existing date fields are shown.
          </Alert>

          {/* New dates input */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Set New Dates</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" type="date"
                    label="Travel Expiry Date"
                    value={newTravelDate}
                    onChange={(e) => setNewTravelDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty to keep unchanged"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" type="date"
                    label="Ticketing Expiry Date"
                    value={newTicketingDate}
                    onChange={(e) => setNewTicketingDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty to keep unchanged"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBulkExtend}
                    disabled={selectedRules.length === 0 || (!newTravelDate && !newTicketingDate)}
                    fullWidth
                    sx={{ height: 40 }}
                  >
                    Extend {selectedRules.length} Selected Rules
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card sx={{ mb: 2, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Filter by Carrier" value={filterCarrier} onChange={e => setFilterCarrier(e.target.value)} placeholder="e.g. AI, U:9" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Filter by Origin" value={filterOrigin} onChange={e => setFilterOrigin(e.target.value)} placeholder="e.g. N:US, N:GB" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Cabin</InputLabel>
                  <Select value={filterCabin} label="Filter by Cabin" onChange={e => setFilterCabin(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ECONOMY">Economy</MenuItem>
                    <MenuItem value="BUSINESS">Business</MenuItem>
                    <MenuItem value="PREMIUM">Premium</MenuItem>
                    <MenuItem value="FIRST">First</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>

          {/* Rules table */}
          <Card>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRules.length === datedRules.length && datedRules.length > 0}
                        indeterminate={selectedRules.length > 0 && selectedRules.length < datedRules.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Seq#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Carrier</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Travel Effective</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Travel Discontinue</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ticketing Discontinue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datedRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No rules with date fields found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    datedRules.map(rule => {
                      const cGroup = carrierGroupOf(rule.carrier);
                      const oGroup = rule.origin ? locationGroupOf(rule.origin) : null;
                      const viaGroup = !!cGroup && !!filterCode && !rule.carrier.toUpperCase().includes(filterCode);
                      return (
                      <Tooltip key={rule.sequenceNum} arrow placement="bottom-start"
                        title={<RuleDetails rule={rule} viaGroup={viaGroup} />}
                        open={hoveredSeq === rule.sequenceNum && !groupHover}
                        disableHoverListener disableFocusListener disableTouchListener>
                      <TableRow hover selected={selectedRules.includes(rule.sequenceNum)}
                        onMouseEnter={() => setHoveredSeq(rule.sequenceNum)}
                        onMouseLeave={() => setHoveredSeq(s => (s === rule.sequenceNum ? null : s))}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRules.includes(rule.sequenceNum)}
                            onChange={() => handleSelectRule(rule.sequenceNum)}
                          />
                        </TableCell>
                        <TableCell><strong>{rule.sequenceNum}</strong></TableCell>
                        <TableCell>
                          {cGroup ? (
                            <Tooltip arrow placement="right"
                              title={<GroupMembers title={`Carrier group ${rule.carrier}`} members={cGroup.carriers} highlight={filterCode} />}>
                              <Chip label={viaGroup ? `${rule.carrier} (has ${filterCode})` : rule.carrier} size="small"
                                color={viaGroup ? 'warning' : 'default'} variant={viaGroup ? 'outlined' : 'filled'}
                                sx={{ cursor: 'help' }}
                                onMouseEnter={() => setGroupHover(true)} onMouseLeave={() => setGroupHover(false)} />
                            </Tooltip>
                          ) : (
                            <Chip label={rule.carrier} size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {oGroup ? (
                            <Tooltip arrow placement="right" title={<GroupMembers title={`Location group ${rule.origin}`} members={oGroup.locations} />}>
                              <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}
                                onMouseEnter={() => setGroupHover(true)} onMouseLeave={() => setGroupHover(false)}>{rule.origin}</span>
                            </Tooltip>
                          ) : (rule.origin || '—')}
                        </TableCell>
                        <TableCell>
                          {rule.feePercent != null ? `${rule.feePercent}%` : rule.feeAmount || (rule.suppress === 'true' ? 'SUPPRESS' : '—')}
                        </TableCell>
                        <TableCell>{rule.travelDateEffective || '—'}</TableCell>
                        <TableCell>
                          <Chip label={rule.travelDateDiscontinue || 'None'} size="small"
                            color={rule.travelDateDiscontinue && new Date(rule.travelDateDiscontinue) < new Date() ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{rule.ticketingDateDiscontinue || '—'}</TableCell>
                      </TableRow>
                      </Tooltip>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {/* Tab 1: Carrier Group Management */}
      {tabValue === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Add or remove airlines from carrier groups. This is useful when you need to include/exclude specific airlines for certain origin countries.
          </Alert>

          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Carrier Group</InputLabel>
                    <Select value={selectedGroup} label="Select Carrier Group" onChange={e => setSelectedGroup(e.target.value)}>
                      {carrierGroups.map(g => (
                        <MenuItem key={g.tableNum} value={g.tableNum}>
                          Group #{g.tableNum} ({g.carriers.length} carriers)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {currentGroup && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Carrier Group #{currentGroup.tableNum} — {currentGroup.carriers.length} carriers
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                    {currentGroup.carriers.map(c => (
                      <Chip key={c} label={c} color="primary" variant="outlined" />
                    ))}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Airline</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small" label="IATA Code" value={carrierToAdd}
                          onChange={e => setCarrierToAdd(e.target.value)}
                          helperText="2-letter code (e.g. AA)"
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCarrier(); } }}
                        />
                        <Button variant="contained" onClick={handleAddCarrier} sx={{ height: 40 }}>Add</Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Remove Airline</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Carrier</InputLabel>
                          <Select value={carrierToRemove} label="Carrier" onChange={e => setCarrierToRemove(e.target.value)}>
                            {currentGroup.carriers.map(c => (
                              <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button variant="outlined" color="error" onClick={handleRemoveCarrier} sx={{ height: 40 }}>Remove</Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}
